/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file chatHub.js
 * @description
 * Pure, dependency-free logic of the unified messenger hub (ChatHomeScreen):
 * tab model + visibility, channel partition into Chats / Channels (with grouped
 * channel sub-sections), All / Unread filters, per-tab + combined badge math,
 * the `GET /chat/threads` list mapping, meeting helpers (active participants,
 * duration) and the meeting-link helpers (audio-only hash flag, invite e-mail
 * parsing, lobby event normalisation).
 *
 * No React / React Native imports — everything here is unit-tested in
 * `app/tests/teamChatHub.test.js`.
 */

import {arr, channelTitle, idOf, isChannelMuted, isMailThreadChannel, personName, textOf, timeOf} from './chatShapes';

/** Hub tabs in display order. */
export const HUB_TABS = ['chats', 'channels', 'meetings', 'ai', 'support'];

/** Tabs that belong to the Team Chat module (hidden when chat is off). */
export const TEAM_CHAT_TABS = ['chats', 'channels', 'meetings'];

/** Filter chips of the conversation tabs. */
export const HUB_FILTERS = ['all', 'unread', 'thread'];

/** Channel sub-sections of the Channels tab, in display order. */
export const CHANNEL_GROUPS = ['mail', 'public', 'private', 'record', 'customer'];

/** Width (dp) from which the tab bar renders as an Odoo-style left rail. */
export const HUB_RAIL_BREAKPOINT = 768;

/** Simple e-mail shape check (the backend validates for real). */
const EMAIL_RE = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]{2,}$/;

/**
 * Whether a channel is a person-to-person conversation (Chats tab).
 * @param {Object} channel
 * @returns {boolean}
 */
export const isConversationChannel = (channel) => channel?.type === 'direct' || channel?.type === 'group';

/**
 * Channel sub-section of the Channels tab.
 * @param {Object} channel
 * @returns {'mail'|'public'|'private'|'record'|'customer'}
 */
export const channelGroupOf = (channel) => {
    switch (channel?.type) {
        case 'private':
            return 'private';
        case 'record':
            return isMailThreadChannel(channel) ? 'mail' : 'record';
        case 'customer':
            return 'customer';
        default:
            return 'public';
    }
};

/**
 * Last activity timestamp (ms) of a channel.
 * @param {Object} channel
 * @returns {number}
 */
export const channelActivityAt = (channel) => timeOf(channel?.lastMessageAt)
    || timeOf(channel?.lastMessagePreview?.at)
    || timeOf(channel?.createdAt);

/**
 * Sort: starred first, then last activity (newest first). Never mutates.
 * @param {Array} channels
 * @returns {Array}
 */
export const sortHubChannels = (channels) => arr(channels).slice().sort((a, b) => {
    const starDiff = (b?.me?.starred ? 1 : 0) - (a?.me?.starred ? 1 : 0);
    if (starDiff !== 0) {
        return starDiff;
    }

    return channelActivityAt(b) - channelActivityAt(a);
});

/**
 * Effective unread count of a channel for badges (muted → mentions only).
 * @param {Object} channel
 * @param {number} [now]
 * @returns {number}
 */
export const channelUnread = (channel, now = Date.now()) => {
    if (!channel?.me || channel.archived || channel.me.hidden) {
        return 0;
    }
    const mentions = Number(channel.me.mentionCount) || 0;

    // Same rule as computeUnreadTotals (chatShapes) so tab badges add up to the header badge.
    return isChannelMuted(channel, now) ? mentions : (Number(channel.me.unreadCount) || 0);
};

/**
 * Split the visible channels into the Chats tab (EVERY open conversation —
 * direct, group and joined channels, like the main chat screen) and the
 * Channels tab (non-direct channels only). Hidden / archived channels are dropped.
 * @param {Array} channels
 * @returns {{chats: Array, channels: Array}}
 */
export const partitionHubChannels = (channels) => {
    const chats = [];
    const rest = [];
    arr(channels).forEach((channel) => {
        if (!channel || !channel._id || channel.archived || channel.me?.hidden) {
            return;
        }
        chats.push(channel);
        if (!isConversationChannel(channel)) {
            rest.push(channel);
        }
    });

    return {chats: sortHubChannels(chats), channels: sortHubChannels(rest)};
};

/**
 * Apply the All / Unread chip + a free-text needle to a channel list.
 * @param {Array} channels
 * @param {{filter?: string, query?: string, userId?: string}} [options]
 * @returns {Array}
 */
export const filterHubChannels = (channels, {filter = 'all', query = '', userId = null} = {}) => {
    const needle = String(query || '').trim().toLowerCase();

    return arr(channels).filter((channel) => {
        if (filter === 'unread' && channelUnread(channel) <= 0) {
            return false;
        }
        if (!needle) {
            return true;
        }
        const haystack = [
            channelTitle(channel, userId),
            textOf(channel?.topic),
            textOf(channel?.lastMessagePreview?.text),
            textOf(channel?.lastMessagePreview?.senderName),
        ].join(' ').toLowerCase();

        return haystack.includes(needle);
    });
};

/**
 * Group Channels-tab channels into sub-sections (empty groups dropped).
 * @param {Array} channels Already filtered + sorted.
 * @returns {Array<{key: string, data: Array}>}
 */
export const groupHubChannels = (channels) => {
    const buckets = {};
    arr(channels).forEach((channel) => {
        const key = channelGroupOf(channel);
        buckets[key] = buckets[key] || [];
        buckets[key].push(channel);
    });

    return CHANNEL_GROUPS
        .filter((key) => arr(buckets[key]).length > 0)
        .map((key) => ({key, data: buckets[key]}));
};

/**
 * Which hub tabs are shown.
 * @param {{teamChatVisible?: boolean, aiEnabled?: boolean, supportEnabled?: boolean}} gates
 * @returns {string[]}
 */
export const visibleHubTabs = ({teamChatVisible = false, aiEnabled = false, supportEnabled = true} = {}) => HUB_TABS.filter((tab) => {
    if (TEAM_CHAT_TABS.includes(tab)) {
        return !!teamChatVisible;
    }
    if (tab === 'ai') {
        return !!aiEnabled;
    }

    return !!supportEnabled;
});

/**
 * Resolve the active tab: the requested one when visible, else the first visible.
 * @param {string} requested
 * @param {string[]} tabs Visible tabs.
 * @returns {string|null}
 */
export const resolveHubTab = (requested, tabs) => {
    const list = arr(tabs);
    if (requested && list.includes(requested)) {
        return requested;
    }

    return list[0] || null;
};

/**
 * Per-tab badge counts + combined totals.
 * @param {{channels?: Array, supportUnread?: number, liveMeetings?: number}} input
 * @returns {{chats: number, channels: number, meetings: number, ai: number, support: number,
 *   mentions: number, teamTotal: number, total: number}}
 */
export const computeHubBadges = ({channels = [], supportUnread = 0, liveMeetings = 0} = {}) => {
    const now = Date.now();
    const result = {chats: 0, channels: 0, meetings: 0, ai: 0, support: 0, mentions: 0, teamTotal: 0, total: 0};
    arr(channels).forEach((channel) => {
        const unread = channelUnread(channel, now);
        if (!unread) {
            return;
        }
        // Chats lists every conversation, so it carries the full team unread.
        result.chats += unread;
        if (!isConversationChannel(channel)) {
            result.channels += unread;
        }
        result.mentions += Number(channel.me?.mentionCount) || 0;
    });
    result.meetings = Math.max(0, Number(liveMeetings) || 0);
    result.support = Math.max(0, Number(supportUnread) || 0);
    result.teamTotal = result.chats;
    result.total = result.teamTotal + result.support;

    return result;
};

/**
 * Combined unread for every chat entry point (team chat + live support).
 * @param {number} teamUnread
 * @param {number} supportUnread
 * @returns {number}
 */
export const combinedUnread = (teamUnread, supportUnread) => Math.max(0, Number(teamUnread) || 0) + Math.max(0, Number(supportUnread) || 0);

/**
 * Support unread = `unreadForUser` summed over the open (non-closed) conversations.
 * @param {Array} conversations `GET /support-chat/conversations/mine` items.
 * @returns {number}
 */
export const supportUnreadFrom = (conversations) => arr(conversations).reduce((sum, conversation) => {
    if (!conversation || conversation.status === 'closed' || conversation.locked) {
        return sum;
    }

    return sum + Math.max(0, Number(conversation.unreadForUser) || 0);
}, 0);

/**
 * Map `GET /chat/threads` → render rows (invalid items dropped, newest reply first).
 * @param {*} data `{items:[{root, channel, replyCount, lastReplyAt, unreadCount}]}` or a bare array.
 * @returns {Array<{key: string, rootId: string, channelId: string, channel: Object, channelName: string,
 *   channelType: string, senderName: string, text: string, replyCount: number, lastReplyAt: number, unreadCount: number}>}
 */
export const mapThreadItems = (data) => {
    const items = Array.isArray(data) ? data : arr(data?.items);

    return items
        .map((item) => {
            const root = item?.root && typeof item.root === 'object' ? item.root : null;
            const rootId = idOf(root);
            const channelRaw = item?.channel && typeof item.channel === 'object' ? item.channel : null;
            const channelId = idOf(channelRaw) || idOf(root?.channel);
            if (!rootId || !channelId) {
                return null;
            }
            const text = textOf(root?.text).replace(/\s+/g, ' ').trim();

            return {
                key: `${channelId}:${rootId}`,
                rootId,
                channelId,
                channel: channelRaw ? {...channelRaw, _id: channelId} : {_id: channelId},
                channelName: textOf(channelRaw?.name),
                channelType: textOf(channelRaw?.type),
                senderName: personName(root?.sender),
                text,
                replyCount: Math.max(0, Number(item?.replyCount) || 0),
                lastReplyAt: timeOf(item?.lastReplyAt) || timeOf(root?.createdAt),
                unreadCount: Math.max(0, Number(item?.unreadCount) || 0),
            };
        })
        .filter(Boolean)
        .sort((a, b) => b.lastReplyAt - a.lastReplyAt);
};

/**
 * Keep the threads that belong to a tab (Chats = all, Channels = non-direct/group only).
 * @param {Array} threads Output of {@link mapThreadItems}.
 * @param {'chats'|'channels'} tab
 * @returns {Array}
 */
export const threadsForTab = (threads, tab) => arr(threads).filter((thread) => {
    const conversation = thread.channelType === 'direct' || thread.channelType === 'group';

    return tab === 'chats' || !conversation;
});

/**
 * Participants still in the meeting (no `leftAt`), de-duplicated by user.
 * @param {Object} meeting
 * @returns {Array<{user: string, name: string}>}
 */
export const activeParticipants = (meeting) => {
    const seen = new Set();

    return arr(meeting?.participants).filter((participant) => {
        const key = idOf(participant?.user) || personName({name: participant?.name});
        if (!participant || participant.leftAt || !key || seen.has(key)) {
            return false;
        }
        seen.add(key);

        return true;
    }).map((participant) => ({user: idOf(participant.user), name: personName({name: participant.name})}));
};

/**
 * Distinct participants who ever joined (ended-meeting summary).
 * @param {Object} meeting
 * @returns {number}
 */
export const participantCount = (meeting) => new Set(
    arr(meeting?.participants).map((participant) => idOf(participant?.user) || personName({name: participant?.name})).filter(Boolean),
).size;

/**
 * Meeting length in whole minutes (≥1 once started; 0 when unknown).
 * @param {Object} meeting
 * @param {number} [now]
 * @returns {number}
 */
export const meetingDurationMinutes = (meeting, now = Date.now()) => {
    const start = timeOf(meeting?.startedAt);
    if (!start) {
        return 0;
    }
    const end = timeOf(meeting?.endedAt) || now;
    if (end <= start) {
        return 0;
    }

    return Math.max(1, Math.round((end - start) / 60000));
};

/**
 * Split minutes into hours + minutes for a localized label.
 * @param {number} minutes
 * @returns {{hours: number, minutes: number}}
 */
export const splitDuration = (minutes) => {
    const total = Math.max(0, Math.round(Number(minutes) || 0));

    return {hours: Math.floor(total / 60), minutes: total % 60};
};

/**
 * Relative-date bucket of a timestamp (the UI formats each kind).
 * @param {*} value
 * @param {number} [now]
 * @returns {{kind: 'none'|'time'|'yesterday'|'weekday'|'date', ms: number}}
 */
export const relativeDateKind = (value, now = Date.now()) => {
    const ms = timeOf(value);
    if (!ms) {
        return {kind: 'none', ms: 0};
    }
    const startOfDay = (time) => {
        const date = new Date(time);
        date.setHours(0, 0, 0, 0);

        return date.getTime();
    };
    const dayDiff = Math.round((startOfDay(now) - startOfDay(ms)) / 86400000);
    if (dayDiff <= 0) {
        return {kind: 'time', ms};
    }
    if (dayDiff === 1) {
        return {kind: 'yesterday', ms};
    }
    if (dayDiff < 7) {
        return {kind: 'weekday', ms};
    }

    return {kind: 'date', ms};
};

/**
 * Add the audio-only flag (`cam=0`) to the meeting join URL's HASH (the admin
 * meeting page reads it when present; harmless otherwise). Idempotent.
 * @param {string} url
 * @param {boolean} audioOnly
 * @returns {string}
 */
export const withAudioOnlyFlag = (url, audioOnly) => {
    const raw = typeof url === 'string' ? url : '';
    if (!raw || !audioOnly) {
        return raw;
    }
    const hashIndex = raw.indexOf('#');
    if (hashIndex < 0) {
        return `${raw}#cam=0`;
    }
    const hash = raw.slice(hashIndex + 1);
    if (/(^|&)cam=0(&|$)/.test(hash)) {
        return raw;
    }

    return hash ? `${raw}&cam=0` : `${raw}cam=0`;
};

/**
 * Parse a free-text list of e-mails (comma / semicolon / whitespace separated).
 * @param {string} text
 * @returns {{valid: string[], invalid: string[]}}
 */
export const parseEmailList = (text) => {
    const valid = [];
    const invalid = [];
    String(text || '').split(/[\s,;]+/).map((part) => part.trim()).filter(Boolean).forEach((part) => {
        const email = part.toLowerCase();
        if (!EMAIL_RE.test(email)) {
            invalid.push(part);
        } else if (!valid.includes(email)) {
            valid.push(email);
        }
    });

    return {valid, invalid};
};

/**
 * Normalise a `chat:meeting:lobby` socket / push payload.
 * @param {*} payload `{meetingId, guest:{_id|id, name}}`.
 * @returns {{meetingId: string, guestId: string, guestName: string}|null}
 */
export const normalizeLobbyEvent = (payload) => {
    const meetingId = idOf(payload?.meetingId || payload?.meeting);
    const guest = payload?.guest && typeof payload.guest === 'object' ? payload.guest : null;
    const guestId = idOf(guest?._id || guest?.id || guest?.guestId || payload?.guestId);
    if (!meetingId || !guestId) {
        return null;
    }

    return {
        meetingId,
        guestId,
        guestName: personName(guest) || textOf(guest?.displayName) || textOf(guest?.email),
    };
};

/**
 * Map `GET /chat/meetings/{id}/lobby` into normalised waiting guests.
 * @param {*} data `{items:[guest]}` / `{guests:[...]}` / bare array.
 * @param {string} meetingId
 * @returns {Array<{meetingId: string, guestId: string, guestName: string}>}
 */
export const mapLobbyGuests = (data, meetingId) => {
    let list = arr(data?.items);
    if (Array.isArray(data)) {
        list = data;
    } else if (!list.length) {
        list = arr(data?.guests);
    }

    return list
        .filter((guest) => !guest?.status || guest.status === 'waiting')
        .map((guest) => normalizeLobbyEvent({meetingId, guest}))
        .filter(Boolean);
};
